import React, { useState,useEffect,useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next'
import { unitState } from '../../plugins/recoil';
import { mmHgToKPa, celsiusToFahrenheit, percentTommHg,percentToKpa,mmHgToPercent,valueStringForPlayer, checkEmptyValue, checkIBPZero } from '../../plugins/common'
import axios from '../../plugins/axios'
import moment from "moment";
import Wave from "./waveform/wave";
import AlarmType from '../../assets/data/AlarmType.json'; // Alarm Type 정의

import "./A4.scss";

const waveType = ['LEADⅠ','LEADⅡ', 'LEADⅢ','CHEST','PLETH','RESP','IBP1','IBP2','CAPNO']; //PLETH가 SpO2, RESP가 RR이면서 Respiration Rate

function Waveform() {
    const { t, i18n } = useTranslation()
    const [searchParams] = useSearchParams();
    const isFirstRender = useRef(true);    
    const _date = searchParams.get('date');    
    const [waveDatas, setWaveDatas] = useState([]);
    const [printType,setPrintType] = useState(10); // 30초, 15초, 10초        
    const [deviceInfo, setDeviceInfo] = useState() // 선택한 디바이스 정보  
    const [patientInfo, setPatientInfo] = useState() // 선택한 환자 정보    
    const [tabularTrend, setTabularTrend] = useState()
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [isPageReady, setIsPageReady] = useState(false);
    const [alarmMsg, setAlarmMsg] = useState([]); // 알람 메시지 상태 추가

    const [units, setUnits] = useRecoilState(unitState)
    const unitsRef = useRef(units);

    // Unit 재 세팅
    const getUnits = async () => {
        try{
            const unitRes = await axios.get(`/measure/parameter/categories`)
            console.log('unitRes : ', unitRes);
            if (unitRes.code != '200') throw unitRes            
            setUnitInfo(unitRes.data.categories)
            return true;
        }
        catch(err){            
            console.warn(err)
            return false;
        }
    }

    const setUnitInfo = (categories) => {
        if (!categories) return
        let nibpUnit = categories.find(category => category.name === 'NIBP')
        nibpUnit = nibpUnit.unit || nibpUnit.defaultUnit

        let ibpUnit = categories.find(category => category.name === 'IBP')
        ibpUnit = ibpUnit.unit || ibpUnit.defaultUnit

        let co2Unit = categories.find(category => category.name === 'CO2')
        co2Unit = co2Unit.unit || co2Unit.defaultUnit

        let tempUnit = categories.find(category => category.name === 'TEMP')
        tempUnit = tempUnit.unit || tempUnit.defaultUnit

        let gasUnit = categories.find(category => category.name === 'GAS')
        gasUnit = gasUnit.unit || gasUnit.defaultUnit

        const newUnits = {
            ...units,
            'NIBP': nibpUnit,
            'IBP': ibpUnit,
            'IBP1': ibpUnit,
            'IBP2': ibpUnit,
            'IBP3': ibpUnit,
            'IBP4': ibpUnit,
            'EtCO2': co2Unit,
            'InCO2': co2Unit,
            'Temp': tempUnit,
            'Temp1': tempUnit,
            'Temp2': tempUnit,
            'GAS': gasUnit
        }

        unitsRef.current = newUnits;  // ref에 즉시 반영 (동기)
        setUnits(newUnits);           // Recoil state도 업데이트 (비동기)
    }

    // ✅ Print Type 가져오기
    const getPrintType = async () => {
        try{
            const res = await axios.get('/user/me')
            if (res.code != '200') throw res

            //언어 세팅
            i18n.changeLanguage(res.data.language);
            setPrintType(res.data.printOutIntervalSeconds);
        }
        catch(err){
            console.warn(err);
        }
    }

    // ✅ Device & Patient 정보 가져오기
    const getReportInfo = async () => {
        try{
            const deviceId = searchParams.get('deviceid'); // 디폴트 값 설정

            const resDevice = await axios.get(`/device/${deviceId}`);
            if (resDevice.code != '200') throw resDevice            

            let patientData = {};

            // Device 정보는 있어도 Patient 정보가 없을 수 있음.
            if(resDevice?.data?.patientId != '' && resDevice?.data?.patientId != null && resDevice?.data?.patientId != undefined){
                try {                    
                    const resPatient = await axios.get(`/patient/${resDevice?.data?.patientId}`);
                    
                    if (resPatient && resPatient.data) {
                        patientData = resPatient.data;
                    }
                } catch (patientError) {
                    console.warn('환자 정보 가져오기 실패:', patientError);
                }
            }                   

            setDeviceInfo(resDevice.data);
            setPatientInfo(patientData);

            return true;
        }
        catch(err){
            console.warn(err);
            return false;
        }
    }

    // ✅ Trend 데이터 가져오기
    const getTabularData = async () => {
        try{
            const date = _date;    
            const trendRes = await axios.get(`/trend/v2/vitals?serialNo=${deviceInfo?.serial}&startTime=${moment.utc(date).subtract(15, 'seconds').format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment.utc(date).add(15, 'seconds').format('YYYY-MM-DDTHH:mm:ss')}Z&includeStatus=true`);            
            if (trendRes.code != '200') throw trendRes
            console.log('[Print Trend Wave] trendRes : ', trendRes);

            const alarmRes = await axios.get(`/trend/v2/alarms?serialNo=${deviceInfo?.serial}&startTime=${moment.utc(date).subtract(15, 'seconds').format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment.utc(date).add(15, 'seconds').format('YYYY-MM-DDTHH:mm:ss')}Z`);            
            if (alarmRes.code != '200') throw alarmRes
            console.log('[Print Trend Wave] alarmRes : ', alarmRes);

            /*            
                //#region 
                함수 기능 리스트 업
                    - TrendRes의 데이터를 중 측정된 시간이 true인 경우에만 데이터를 표기 해야되는 함수를 만들어야 함.
                      그 중 표기해야되는 Trend List는 "hr", "arrhythmia", "spo2Percent", "rrRpm", "nibp", "ibp1", "ibp2", "temp1C", "temp2C", "awrrRpm", "etCo2" 이다 

                    - 아래는 Trend Data를 체크하는 프로세스를 정리한 내용
                        1. trendRes에서 points와 statusSegments를 가져옴
                        2. points의 timestamp기준으로 Trend List의 각각의 데이터가 측정했는지 체크한다.
                            2-1. points의 timestamp이 statusSegments의 startTime과 endTime 사이에 있는지 체크하고 measured가 true이면 값을 표기해준다.                                                        
                            2-2  points의 timestamp이 statusSegments의 startTime과 endTime 사이에 있는지 체크하고 measured가 false이면 값이 없는 것으로 간주하고 '-'로 표기한다.
                            2-3. 만약 points에 Trend List 데이터가 없으면 값이 없는 것으로 간주하고 '-'로 표기한다 (예를들면, spo2Percent가 points에 없으면 spo2Percent는 '-'로 표기한다.)
                            2-4. 데이터를 전부 timestamp단위로 체크하여 표기해야하므로 2-1, 2-2, 2-3의 과정을 반복한다. (예를 들어, hr이 points에 있지만 statusSegments에 측정된 시간이 없으면 hr은 '-'로 표기한다.)
                        3. 2번에 대한 과정 중 2-1의 경우에 데이터를 표기 할때 unit를 판단하여 데이터 값을 변환해야 하면 값을 변환해서 저장한다. 저장 로직은 convertUnit 함수를 사용한다.

                        4. Alarm이 발생한 데이터를 체크 하여 데이터를 추가한다.
                            4-1. alarmRes에 alarms에 데이터가 있는지 확인한다.
                            4-2. alarms에 데이터가 있다면, 먼저 setAlarmMsg에 eventName만 추출하여 전부 저장한다. (Alarm Message 표기를 위함)
                            4-3. alarms에 데이터가 있다면, 각 알람의 eventCode를 확인하여 해당하는 Trend List의 필드에 알람 정보를 추가한다.
                //endregion
            */
            
            // ✅ 1~4번: trendRes + alarmRes 기반 Trend 데이터 가공 (측정 상태 + 단위 변환 + 알람 병합)
            const finalData = processTrendData(trendRes.data, alarmRes.data);
            setTabularTrend(finalData);
            console.log('[Print Trend Wave] finalData (1~4번 완료) : ', finalData);            

            return true;
        }
        catch(err){
            console.warn(err);
            return false;
        }
    }

    // ✅ 1~4번 통합: TrendRes + AlarmRes 데이터를 가공하는 함수
    // 1. points와 statusSegments 추출
    // 2. statusSegments 기반 측정 상태 체크 + 단위 변환
    // 3. 알람 데이터 병합 (alarmFlags, alarmMsg)
    const processTrendData = (trendData, alarmData) => {
        const points = trendData?.points || [];
        const statusSegments = trendData?.statusSegments || [];
        const alarms = alarmData?.alarms || [];

        // ── Trend List 정의 ──
        const TREND_LIST_CONFIG = {
            hr:           { metric: 'hr',           fields: ['hr'],                            convertKey: 'HR' },
            arrhythmia:   { metric: 'arrhythmia',   fields: ['arrhythmia'],                    convertKey: 'arrhythmia' },
            spo2Percent:  { metric: 'spo2Percent',  fields: ['spo2Percent'],                   convertKey: 'SpO2' },
            rrRpm:        { metric: 'rrRpm',        fields: ['rrRpm'],                         convertKey: 'RR' },
            nibp:         { metric: 'nibp',         fields: ['nibpSys', 'nibpDia', 'nibpMap'], convertKey: 'NIBP' },
            ibp1:         { metric: 'ibp1',         fields: ['ibp1Sys', 'ibp1Dia', 'ibp1Map'], convertKey: 'IBP1' },
            ibp2:         { metric: 'ibp2',         fields: ['ibp2Sys', 'ibp2Dia', 'ibp2Map'], convertKey: 'IBP2' },
            temp1C:       { metric: 'temp1C',       fields: ['temp1C'],                        convertKey: 'Temp1' },
            temp2C:       { metric: 'temp2C',       fields: ['temp2C'],                        convertKey: 'Temp2' },
            awrrRpm:      { metric: 'awrrRpm',      fields: ['awrrRpm'],                       convertKey: 'awRR' },
            etCo2:        { metric: 'etCo2',        fields: ['etCo2'],                         convertKey: 'EtCO2' },
        };

        // ── AlarmType 필드명 → trendKey 정규화 매핑 ──
        const ALARM_FIELD_MAP = {
            'hr': 'hr',
            'arrhythmia': 'arrhythmia',
            'SpO2': 'spo2Percent',
            'spo2Percent': 'spo2Percent',
            'rrRpm': 'rrRpm',
            'nibp': 'nibp',
            'ibp1': 'ibp1',
            'ibp2': 'ibp2',
            'temp1C': 'temp1C',
            'temp2C': 'temp2C',
            'awrrRpm': 'awrrRpm',
            'etco2': 'etCo2',
            'etCo2': 'etCo2',
        };

        // ── 4-2: 알람 메시지 세팅 ──
        if (alarms.length) {
            const eventNames = [...new Set(alarms.map(alarm => `${alarm.alarmType} : ${alarm.eventName}`))];
            setAlarmMsg(eventNames);
            console.log('[Print Trend Wave] alarmMsg : ', eventNames);
        } else {
            setAlarmMsg([]);
        }       

        const alarmTypeMap = AlarmType[0];

        // ── 측정 상태 체크: statusSegments [from, to) half-open interval ──
        const isMeasured = (timestamp, metric) => {
            const ts = new Date(timestamp).getTime();
            const matchedSegment = statusSegments.find(seg => {
                if (seg.metric !== metric) return false;
                const from = new Date(seg.from).getTime();
                const to = new Date(seg.to).getTime();
                return ts >= from && ts < to;
            });
            if (!matchedSegment) return false;
            return matchedSegment.measured;
        };

        // ── points 순회: 1~3번(측정 상태 + 단위 변환) + 4번(알람 병합) 한번에 처리 ──
        const result = points.map(point => {
            const timestamp = point.timestamp;
            const ts = new Date(timestamp).getTime();
            const row = { timestamp };

            // ── 1~3번: Trend List 데이터 가공 ──
            Object.entries(TREND_LIST_CONFIG).forEach(([trendKey, config]) => {
                const { metric, fields, convertKey } = config;
                const measured = isMeasured(timestamp, metric);

                // Temp 계열은 빈 값일 때 '---.-', 나머지는 '---'
                const isTemp = (trendKey === 'temp1C' || trendKey === 'temp2C');
                const emptyValue = isTemp ? '---.-' : '---';

                if (fields.length === 1) {
                    const rawValue = point[fields[0]];
                    if (rawValue === undefined || rawValue === null || !measured) {
                        row[trendKey] = emptyValue;
                    } else {
                        const converted = convertUnit(convertKey, rawValue);
                        row[trendKey] = isTemp ? Number(converted).toFixed(1) : converted;
                    }
                } else {
                    const isNIBP = trendKey === 'nibp';
                    const isIBP = trendKey === 'ibp1' || trendKey === 'ibp2';
                    const groupResult = {};
                    fields.forEach(fieldName => {
                        const rawValue = point[fieldName];
                        if (rawValue === undefined || rawValue === null || !measured) {
                            groupResult[fieldName] = '---';
                        } else if (isNIBP || isIBP) {
                            const checked = checkIBPZero(rawValue, '', '', isNIBP);
                            groupResult[fieldName] = checked === '-' ? '---' : convertUnit(convertKey, rawValue);
                        } else {
                            groupResult[fieldName] = convertUnit(convertKey, rawValue);
                        }
                    });
                    row[trendKey] = groupResult;
                }
            });

            // ── 4번: 알람 정보 병합 ──
            const matchedAlarms = alarms.filter(alarm => {
                const alarmStart = new Date(alarm.startTime).getTime();
                const alarmEnd = new Date(alarm.endTime).getTime();
                return ts >= alarmStart && ts <= alarmEnd;
            });

            const alarmFlags = {};
            matchedAlarms.forEach(alarm => {
                const trendFields = alarmTypeMap[alarm.eventCode];
                if (!trendFields) return;

                const fieldList = Array.isArray(trendFields) ? trendFields : [trendFields];
                fieldList.forEach(field => {
                    const normalizedField = ALARM_FIELD_MAP[field];
                    if (normalizedField) {
                        if (!alarmFlags[normalizedField]) {
                            alarmFlags[normalizedField] = [];
                        }
                        alarmFlags[normalizedField].push({
                            id: alarm.id,
                            eventCode: alarm.eventCode,
                            eventName: alarm.eventName,
                            eventPriority: alarm.eventPriority,
                            alarmType: alarm.alarmType
                        });
                    }
                });
            });

            row.alarmFlags = alarmFlags;
            row.alarms = matchedAlarms;

            // outOfRange 처리: eventName이 'outOfRange'인 알람의 alarmType으로 파라미터 판별 (IBP, SpO2만 적용)
            const OUT_OF_RANGE_MAP = {
                'SPO2': ['spo2Percent'],
                'IBP1': ['ibp1'],
                'IBP2': ['ibp2'],
            };
            const outOfRangeFlags = {};
            matchedAlarms.forEach(alarm => {
                if (alarm.eventName === 'outOfRange') {
                    const fields = OUT_OF_RANGE_MAP[alarm.alarmType];
                    if (fields) {
                        fields.forEach(field => {
                            outOfRangeFlags[field] = true;
                            if (['ibp1', 'ibp2'].includes(field)) {
                                const config = TREND_LIST_CONFIG[field];
                                const groupResult = {};
                                config.fields.forEach(f => { groupResult[f] = 'XXX'; });
                                row[field] = groupResult;
                            } else {
                                row[field] = 'XXX';
                            }
                        });
                    }
                }
            });
            row.outOfRangeFlags = outOfRangeFlags;

            return row;
        });

        return result;
    };

    // 단위 변환 함수
    const convertUnit = (parameter,value) => {
        const targetUnits = unitsRef.current;  // ✅ ref에서 최신 값 참조
        const nibpSeries = ['NIBP']
        const ibpSeries = ['IBP1', 'IBP2', 'IBP3', 'IBP4']
        const co2Series = ['EtCO2', 'InCO2']
        const tempSeries = ['Temp1', 'Temp2']
        const gasSeries = ['GAS']
        const otherSeries = ['HR', 'RR', 'awRR', 'SpO2', 'arrhythmia', 'IPI']

            //함수를 유동적으로 사용해보고 싶어서 써봄.
        const convertFunctions = {
            mmHgToKPa,
            celsiusToFahrenheit,
            percentTommHg,
            percentToKpa,
            mmHgToPercent
        };

        let unit = undefined;
        let functionName = undefined;                

        if (nibpSeries.includes(parameter)) {
            unit = targetUnits["NIBP"];
            if(unit === 'kPa') {
                functionName = 'mmHgToKPa'; // mmHg를 kPa로 변환하는 함수
            }
        } else if (ibpSeries.includes(parameter)) {
            const prefix = parameter.match(/^IBP[1-4]/)?.[0];  // "IBP1", "IBP2" 등 추출
            unit = targetUnits[prefix];
            if(unit === 'kPa') {
                functionName = 'mmHgToKPa'; // mmHg를 kPa로 변환하는 함수
            }
        } else if (co2Series.includes(parameter)) {
            unit = targetUnits[parameter];            
            if(unit === 'kPa') {
                functionName = 'mmHgToKPa'; // mmHg를 kPa로 변환하는 함수
            }
            else if(unit === '%'){
                functionName = 'mmHgToPercent'; // mmHg를 %로 변환하는 함수
            }
        } else if (tempSeries.includes(parameter)) {
            unit = targetUnits[parameter];
            if(unit === '°F') {
                functionName = 'celsiusToFahrenheit'; // 섭씨를 화씨로 변환하는 함수
            }
        } 
        else if (gasSeries.includes(parameter)) {            
            unit = targetUnits["GAS"];            
            if(unit === 'mmHg') {
                functionName = 'percentTommHg'; // mmHg를 kPa로 변환하는 함수
            }
            else if(unit === 'kPa'){
                functionName = 'percentToKpa';
            }
        } else if(otherSeries.includes(parameter)) {
            unit = targetUnits[parameter];
        }

        let label = value;
        if (functionName && typeof convertFunctions[functionName] === 'function') {
            // v2 API: 모든 값이 개별 숫자로 전달되므로 직접 변환
            label = convertFunctions[functionName](value);
        }
        return label;
    }

    //Wave 데이터 가져오기
    const getWaveData = async () => {
        try{
            const totalWave = [];
            //const date = '2025-07-21T13:55:00'; //시간도 같이 보낸걸로 해야될 듯.
            const date = _date; //시간도 같이 보낸걸로 해야될 듯.            

            for (const type of waveType) {
                const waveRes = await axios.get(`/trend/waveforms?serialNo=${deviceInfo?.serial}&type=${type}&startTime=${moment.utc(date).subtract(15, 'seconds').format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment.utc(date).add(15, 'seconds').format('YYYY-MM-DDTHH:mm:ss')}Z`)
                const fillwave = setTrendWaveList(waveRes.data.waveformData.waveforms,type, moment.utc(date).subtract(15, 'seconds').format('YYYY-MM-DDTHH:mm:ss'), moment.utc(date).add(15, 'seconds').format('YYYY-MM-DDTHH:mm:ss'));                

                // ✅ 여러개 일 경우 한개의 row로 들어가야되니까 이렇게 풀었음. 
                if (Array.isArray(fillwave)) {
                    fillwave.forEach((batch, batchIndex) => {                        
                        totalWave.push(batch); // 각 배치를 개별적으로 추가
                    });
                }
            }
            
            //aVR,aVL,aVF API 가져오기
            const otherWaveRes = await axios.get(`/trend/augmented/waveforms?serialNo=${deviceInfo?.serial}&startTime=${moment.utc(date).subtract(15, 'seconds').format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment.utc(date).add(15, 'seconds').format('YYYY-MM-DDTHH:mm:ss')}Z`)

            for(const leadType of ['avf','avl','avr']){
                const fillwave = setOtherTrendWaveList(otherWaveRes.data.data,leadType, moment.utc(date).subtract(15, 'seconds').format('YYYY-MM-DDTHH:mm:ss'), moment.utc(date).add(15, 'seconds').format('YYYY-MM-DDTHH:mm:ss'));
                if (Array.isArray(fillwave)) {
                    fillwave.forEach((batch, batchIndex) => {
                        totalWave.push(batch); // 각 배치를 개별적으로 추가
                    });
                }
            }            

            // ✅ PrintType에 따라 페이지 나누기.
            const pageData = [];
            const samplesPerPage = 6; // 한 페이지당 보여줄 데이터 개수
            const totalSamples = totalWave.length;
            const chunkSize = samplesPerPage;

            for (let i = 0; i < totalSamples; i += chunkSize) {
                pageData.push(totalWave.slice(i, i + chunkSize));
            }

            console.log('pageData : ',pageData);

            //console.log('pages :', pageData);
            setWaveDatas(pageData);

            return true;
        }
        catch(err){
            console.warn(err); 
            return false;
        }
    }    

    // Trend Wave 빈 데이터 생성 및 추가.
    const setTrendWaveList = (data,trendWaveType, startTime, endTime) => {
        // 시작 시간과 끝 시간을 moment로 변환
        const start = moment.utc(startTime).startOf("second");
        const end = moment.utc(endTime).startOf("second");
    
        // 시작 시간부터 끝 시간까지 초 단위로 빈 데이터 생성
        const allSeconds = [];
        let current = start.clone();
        while (current < end) {
            allSeconds.push(current.format());
            current.add(1, "second");
        }    

        // 실제 데이터를 Timestamp : value 형태로 변환
        const groupedBySecond = data.reduce((acc, item) => {
            const secondKey = moment.utc(item.timestamp).startOf("second").format();  
            acc[secondKey] = item.samples || [];
            return acc;
        }, {});                   

        const completeResult = allSeconds
        .map((second) => ({
            type : trendWaveType,
            createdAt: second,
            wave: groupedBySecond[second] || [], // 데이터가 없으면 빈 배열
        }))
        .reduce((batches, item, index) => {
            const batchIndex = Math.floor(index / printType);
            if (!batches[batchIndex]) {
                batches[batchIndex] = [];
            }
            batches[batchIndex].push(item);
            return batches;
        }, []);

        return completeResult;
    };

    //aVF, aVL, aVR 빈 데이터 생성 및 추가
    const setOtherTrendWaveList = (data,trendWaveType, startTime, endTime) => {
        // 시작 시간과 끝 시간을 moment로 변환
        const start = moment.utc(startTime).startOf("second");
        const end = moment.utc(endTime).startOf("second");       
    
        // 시작 시간부터 끝 시간까지 초 단위로 빈 데이터 생성
        const allSeconds = [];
        let current = start.clone();
        while (current < end) {
            allSeconds.push(current.format());
            current.add(1, "second");
        }
    
        // 실제 데이터를 Timestamp : value 형태로 변환
        const groupedBySecond = data.reduce((acc, item) => {
            const secondKey = moment.utc(item.timestamp).startOf("second").format();  
            acc[secondKey] = item[trendWaveType] || [];
            return acc;
        }, {});

        const completeResult = allSeconds
        .map((second) => ({
            type : trendWaveType,
            createdAt: second,
            wave: groupedBySecond[second] || [], // 데이터가 없으면 빈 배열
        }))
        .reduce((batches, item, index) => {
            const batchIndex = Math.floor(index / printType);
            if (!batches[batchIndex]) {
                batches[batchIndex] = [];
            }
            batches[batchIndex].push(item);
            return batches;
        }, []);

        return completeResult;
    };

    // 프린터 함수
    const handlePrint = () => {
        console.log('프린트 시작');
        
        // 클래스 제거 후 다시 추가 (리프레시 효과)
        document.documentElement.classList.remove("waveform-active");
        document.body.classList.remove("waveform-active");
        
        setTimeout(() => {
            document.documentElement.classList.add("waveform-active");
            document.body.classList.add("waveform-active");
            
            setTimeout(() => {
                console.log('페이지 수:', document.querySelectorAll('.a4-page').length);
                window.print();
            }, 500);
        }, 100);
    };
    
    // 페이지 로드 되고 처리
    useEffect(() => {
        const style = document.createElement("style");
            style.innerHTML = `
            @page {
                size: 2480px 3508px;
                margin: 0;
            }
            `;
        document.head.appendChild(style);

        // 스타일 클래스 추가
        document.documentElement.classList.add("waveform-active");
        document.body.classList.add("waveform-active");        
        
        getPrintType();
        getReportInfo(); //Device & Patient정보 가져오기.

        return () => {
            document.documentElement.classList.remove("waveform-active");
            document.body.classList.remove("waveform-active");
            document.head.removeChild(style); // 라우트 빠져나갈 때 제거
        };
    }, []);

    useEffect(() => {

        if (isFirstRender.current) {
            isFirstRender.current = false; // 초기 렌더링 후 플래그 업데이트
            return;
        }

        const loadAllData = async () => {
            //console.log('데이터 로딩 시작...');
            
            try {
                //Units를 먼저 가져오기 (unitsRef에 즉시 반영)
                const unitsResult = await getUnits(); // Unit 정보 먼저 가져오기                
                const tabularResult = await getTabularData(); // Tabular 데이터 Trend, Alarm 데이터를 가져와야됨.                

                //나머지는 병렬 실행 (unitsRef.current에서 최신 값 참조)
                const [waveResult] = await Promise.all([                    
                    getWaveData(),                    
                ]);

                if (unitsResult && waveResult && tabularResult) {
                    console.log('모든 데이터 로딩 완료');
                    setIsDataLoaded(true);
                } else {
                    console.error('데이터 로딩 실패');
                }
            } catch (error) {
                console.error('데이터 로드 중 에러:', error);
            }
        };

        loadAllData();
    }, [deviceInfo,patientInfo]);    

    // 데이터 로드 완료 후 렌더링 완료 대기
    useEffect(() => {
        if (isDataLoaded && waveDatas.length > 0) {
            //console.log('페이지 렌더링 대기 중...');
            
            // DOM 렌더링 및 Canvas 그리기 완료를 위한 충분한 대기 시간
            const renderTimer = setTimeout(() => {
                //console.log('페이지 렌더링 완료');
                setIsPageReady(true);
            }, 1500); // Canvas 렌더링을 고려한 여유 시간

            return () => clearTimeout(renderTimer);
        }
    }, [isDataLoaded, waveDatas]);

    // 페이지 준비 완료 시 자동 프린트
    useEffect(() => {
        if (isPageReady) {
            // URL 파라미터로 자동 프린트 제어
            const urlParams = new URLSearchParams(window.location.search);
            const autoPrint = urlParams.get('autoPrint');
            const delay = parseInt(urlParams.get('delay')) || 3000;
            
            if (autoPrint === 'true' || autoPrint === null) { // 기본값은 자동 프린트
                setTimeout(() => {
                    handlePrint(); // 자동 프린트 잠시 스탑
                }, delay);
            }
        }
    }, [isPageReady]);

    //인쇄 하거나 취소하고 창 꺼지기
    window.onafterprint = () => {
        setTimeout( () => {
            window.close();
        },1000)
    };

    // 스타일 정의
    const patientInfoFontBold = 'text-black font-medium text-[48px] leading-[110%] font-pretendard';
    const patientInfoFont = 'text-black font-thin text-[36px] leading-[40px] font-pretendard';
    const tableHeaderFont = 'text-black text-center font-pretendard text-[28px] font-medium leading-[110%] bg-[#fff] py-[24px]';
    const tableBodyFont = 'text-black text-center font-pretendard text-[32px] font-light leading-[110%] py-[16px] whitespace-nowrap';
    const tableLine = 'border-b-[1px] border-opacity-50 border-[#5E5E5E]';
    const waveFont = 'text-black font-pretendard text-[28px] font-medium leading-[110%]';

    return (
        <div className="document-container bg-gray-100 p-20 font-sans min-h-screen">
            <div className="a4-page mx-auto bg-white shadow-lg relative box-border">
                <div className="w-full h-full flex flex-col px-[146px] pt-[140px] pb-[66px]">
                    {/* Header */}
                    <div className={`${patientInfoFontBold} w-full flex items-center justify-start mb-[24px] border-b border-[#5E5E5E] pb-[24px]`}>
                        <h1 className="mr-[80px] ">
                            <span>{t('print.common.patientId')} : </span>
                            <span>[{patientInfo?.devicePatientId || '---'}]</span>
                        </h1>
                        <h1 className="text-black font-medium text-[48px] leading-[110%] ">
                            <span>{t('print.common.name')} : </span>
                            <span>[{patientInfo?.name || '---'}]</span>
                        </h1>
                    </div>
                    {/* Patient Info */}
                    <div className={`${patientInfoFont} w-full flex items-center justify-between flex-wrap py-[32px] mb-[96px]`}>
                        <div className="w-full flex mb-[32px]">
                            <div className="mr-[64px]">
                                <strong className={`${patientInfoFont}`}>{t('print.common.admitted')}  :  </strong>
                                {/* <span>{moment().format('YYYY-MM-DD HH:mm')}</span> */}
                                <span>{patientInfo?.createdAt ? moment(patientInfo?.createdAt).format('YYYY-MM-DD HH:mm') : '-'}</span>
                            </div>
                            {/* <div>
                                <strong className={`${patientInfoFont}`}>Location  : </strong>
                                <span>{patientInfo?.address || ''}</span>
                            </div> */}
                        </div>
                        <div className="w-full flex mb-[32px]">
                            <div className="mr-[64px]">
                                <strong className={`${patientInfoFont}`}>{t('print.common.gender')}  :  </strong>
                                <span>{patientInfo?.gender === 'M' ? t('print.common.male') : patientInfo?.gender === 'F' ? t('print.common.female') : t('print.common.notSelected')} , </span>
                            </div>
                            <div className="mr-[64px]">
                                <strong className={`${patientInfoFont}`}>{t('print.common.height')}  :  </strong>
                                <span>{patientInfo?.height || '---'} , </span>
                            </div>
                            <div className="mr-[64px]">
                                <strong className={`${patientInfoFont}`}>{t('print.common.weight')}  :  </strong>
                                <span>{patientInfo?.weight || '--'} , </span>
                            </div>
                            <div className="mr-[64px]">
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.birthday')}  :  </strong>
                                <span>{patientInfo?.birthDate || '------'} , </span>
                            </div>
                            <div>
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.phoneNumber')}  :  </strong>
                                <span>{patientInfo?.phoneNumber || '-----'} , </span>
                            </div>
                        </div>   
                        <div className="w-full flex mb-[32px]">
                            <div className="mr-[64px]">
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.emergencyContactName')}  :  </strong>
                                <span>{patientInfo?.protectorName || '-----'} , </span>
                            </div>
                            <div>
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.phoneNumber')}  :  </strong>
                                <span>{patientInfo?.protectorPhoneNumber || '-----'}</span>
                            </div>
                        </div>
                        <div className="w-full flex mb-[32px] pb-[32px] border-b border-[#5E5E5E] border-opacity-50">
                            <div className="break-all">
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.address')}  :  </strong>
                                <span>{patientInfo?.address || '-----'}</span>
                            </div>
                        </div> 
                        <div className="w-full flex mb-[32px]">
                            <div className="mr-[64px]">
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.doctor')}1 :  </strong>
                                <span>{patientInfo?.doctor1?.doctorName || '----'} , </span>
                            </div>
                            <div className="mr-[64px]">
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.phoneNumber')}  :  </strong>
                                <span>{patientInfo?.doctor1?.doctorPhoneNumber || '-----'}</span>
                            </div>
                            <div className="mr-[64px]">
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.doctor')}2 :  </strong>
                                <span>{patientInfo?.doctor2?.doctorName || '----'} , </span>
                            </div>
                            <div>
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.phoneNumber')}  :  </strong>
                                <span>{patientInfo?.doctor2?.doctorPhoneNumber || '-----'}</span>
                            </div>
                        </div>
                        <div className="w-full flex pb-[32px] border-b border-[#5E5E5E] border-opacity-50">
                            <div className="mr-[64px]">
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.nurse')}1 :  </strong>
                                <span>{patientInfo?.nurse1?.nurseName || '----'} , </span>
                            </div>
                            <div className="mr-[64px]">
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.phoneNumber')}  :  </strong>
                                <span>{patientInfo?.nurse1?.nursePhoneNumber || '-----'}</span>
                            </div>
                            <div className="mr-[64px]">
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.nurse')}2 :  </strong>
                                <span>{patientInfo?.nurse2?.nurseName || '----'} , </span>
                            </div>
                            <div>
                                <strong className={`${patientInfoFont}`}>{t('print.waveForm.phoneNumber')}  :  </strong>
                                <span>{patientInfo?.nurse2?.nursePhoneNumber || '-----'}</span>
                            </div>
                        </div>
                    </div>
                    {/* Waveform Section */}
                    <div>
                        <div className="text-black font-pretendard text-[40px] font-medium leading-[120%] mb-[24px]">
                            <span>{t('print.waveForm.recordTime')} :</span> {moment(_date).format('YY-MM-DD')} {moment(_date).subtract(15, 'seconds').format('HH:mm:ss')} ~ {moment(_date).add(15, 'seconds').format('HH:mm:ss')}
                            <span> (* {t('print.waveForm.alarmSituation')})</span>                            
                        </div>
                        {/* Record Section */}
                        <div className="w-full flex items-center justify-between flex-wrap border-t-[4px] border-[#5E5E5E]">
                            <div title="Table" className="w-full flex flex-col justify-center items-center">
                                <div title="Table Header" className={`${tableLine} w-full flex items-center`}>
                                    <div className={`${tableHeaderFont} flex-1`}>HR/PR({units['HR']})</div>
                                    <div className={`${tableHeaderFont} flex-1`}>Ar</div>
                                    <div className={`${tableHeaderFont} flex-1`}>Sp02({units['SpO2']})</div>
                                    <div className={`${tableHeaderFont} flex-1`}>RR({units['RR']})</div>
                                    <div className={`${tableHeaderFont} flex-[1.8]`}>NIBP({units['NIBP']})</div>
                                    <div className={`${tableHeaderFont} flex-[1.8]`}>IBP1({units['IBP1']})</div>
                                    <div className={`${tableHeaderFont} flex-[1.8]`}>IBP2({units['IBP2']})</div>
                                    <div className={`${tableHeaderFont} flex-1`}>T1({units['Temp1']})</div>
                                    <div className={`${tableHeaderFont} flex-1`}>T2({units['Temp2']})</div>
                                    <div className={`${tableHeaderFont} flex-1`}>awRR({units['awRR']})</div>
                                    <div className={`${tableHeaderFont} flex-1`}>EtCO2({units['EtCO2']})</div> 
                                </div>                                 

                                {
                                    tabularTrend && tabularTrend.map((trend, index) => {
                                        const af = trend.alarmFlags || {};
                                        const oor = trend.outOfRangeFlags || {};
                                        const nibp = trend['nibp'] || {};
                                        const ibp1 = trend['ibp1'] || {};
                                        const ibp2 = trend['ibp2'] || {};
                                        return (
                                            <div key={index} className={`${tableLine} w-full flex items-center`}>
                                                <div className={`${tableBodyFont} flex-1`}>{trend['hr']}{af['hr'] && !oor['hr'] && trend['hr'] !== '---' ? '＊' : ''}</div>
                                                <div className={`${tableBodyFont} flex-1`}>{trend['arrhythmia']}{af['arrhythmia'] && !oor['arrhythmia'] && trend['arrhythmia'] !== '---' ? '＊' : ''}</div>
                                                <div className={`${tableBodyFont} flex-1`}>{trend['spo2Percent']}{af['spo2Percent'] && (oor['spo2Percent'] || trend['spo2Percent'] !== '---') ? '＊' : ''}</div>
                                                <div className={`${tableBodyFont} flex-1`}>{trend['rrRpm']}{af['rrRpm'] && !oor['rrRpm'] && trend['rrRpm'] !== '---' ? '＊' : ''}</div>
                                                <div className={`${tableBodyFont} flex-[1.8]`}>{`${nibp.nibpSys}/${nibp.nibpDia}(${nibp.nibpMap})`}{af['nibp'] && !oor['nibp'] && nibp.nibpSys !== '---' && nibp.nibpDia !== '---' && nibp.nibpMap !== '---' ? '＊' : ''}</div>
                                                <div className={`${tableBodyFont} flex-[1.8]`}>{`${ibp1.ibp1Sys}/${ibp1.ibp1Dia}(${ibp1.ibp1Map})`}{af['ibp1'] && (oor['ibp1'] || (ibp1.ibp1Sys !== '---' && ibp1.ibp1Dia !== '---' && ibp1.ibp1Map !== '---')) ? '＊' : ''}</div>
                                                <div className={`${tableBodyFont} flex-[1.8]`}>{`${ibp2.ibp2Sys}/${ibp2.ibp2Dia}(${ibp2.ibp2Map})`}{af['ibp2'] && (oor['ibp2'] || (ibp2.ibp2Sys !== '---' && ibp2.ibp2Dia !== '---' && ibp2.ibp2Map !== '---')) ? '＊' : ''}</div>
                                                <div className={`${tableBodyFont} flex-1`}>{trend['temp1C']}{af['temp1C'] && !oor['temp1C'] && trend['temp1C'] !== '---.-' ? '＊' : ''}</div>
                                                <div className={`${tableBodyFont} flex-1`}>{trend['temp2C']}{af['temp2C'] && !oor['temp2C'] && trend['temp2C'] !== '---.-' ? '＊' : ''}</div>
                                                <div className={`${tableBodyFont} flex-1`}>{trend['awrrRpm']}{af['awrrRpm'] && !oor['awrrRpm'] && trend['awrrRpm'] !== '---' ? '＊' : ''}</div>
                                                <div className={`${tableBodyFont} flex-1`}>{trend['etCo2']}{af['etCo2'] && !oor['etCo2'] && trend['etCo2'] !== '---' ? '＊' : ''}</div>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        </div>    
                        {/* Alarm Message */}
                        <div className="flex items-start w-full mt-[28px]">
                            <div className="mr-2 flex-shrink-0">
                                <span className="text-black font-pretendard text-[24px] font-normal leading-[110%] opacity-50">[{t('print.waveForm.alarmMessage')}] ➔ </span>
                            </div>
                            <div className="w-full flex">
                                <span className="text-black font-pretendard text-[24px] font-normal leading-[110%] opacity-50 mr-2">
                                    {/* Cannot Analyse ECG. ECG -SVPBs , Cannot Analyse ECG. ECG -SVPBs , Cannot Analyse ECG. ECG -SVPBs , Cannot Analyse ECG. ECG -SVPBs , Cannot Analyse ECG. ECG -SVPBs , Cannot Analyse ECG. ECG -SVPBs */}
                                    {alarmMsg && alarmMsg.join(', ')}
                                </span>
                            </div>                            
                        </div>                                         
                    </div>
                    {/* Footer */}
                    <div className="w-full flex items-center justify-between mt-auto">
                        <div>
                            <span className="text-black text-center font-pretendard text-[24px] font-medium leading-[110%]">{`(${moment().format('YYYY - M - D  H : mm')})`}</span>
                        </div>
                        <div>
                            <span className="text-black text-center font-pretendard text-[24px] font-medium leading-[110%]">Page 1/{waveDatas.length + 1}</span>
                        </div> 
                    </div>
                </div>
            </div>           

            {
                waveDatas && waveDatas.map((waves, index) => {
                    return (
                        <div className="a4-page mx-auto bg-white shadow-lg relative box-border" key={index}>
                            <div className="relative w-full h-full flex flex-col px-[146px] pt-[140px] pb-[66px]">
                                <div className={`${patientInfoFontBold} w-full flex items-center justify-start mb-[24px]`}>
                                    <h1 className="mr-[80px] ">
                                        <span>{t('print.common.patientId')} : </span>
                                        <span>[{patientInfo?.devicePatientId || ''}]</span>
                                    </h1>
                                </div>
                                {
                                    waves && waves.map((wave, index) => {
                                        return (
                                            <div className="w-full flex flex-col mb-[80px]" key={index}>
                                                <div className="w-full flex items-center justify-start mb-[16px]">
                                                    <span className={`${waveFont}`}>{wave[index].type}</span>
                                                </div>                        
                                                <Wave data={wave}/>
                                                <div className="w-full flex items-center justify-between mt-[16px]">
                                                    <span className={`${waveFont}`}>{moment(wave[0].createdAt).local().format('HH : mm : ss')}</span>
                                                    <span className={`${waveFont}`}>{moment(wave[(wave.length-1)].createdAt).local().add(1, 'seconds').format('HH : mm : ss')}</span>
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                                <div className="w-full flex items-center justify-between mt-auto">
                                    <div>
                                        <span className="text-black text-center font-pretendard text-[24px] font-medium leading-[110%]">{`(${moment().format('YYYY - M - D  H : mm')})`}</span>
                                    </div>
                                    <div>
                                        <span className="text-black text-center font-pretendard text-[24px] font-medium leading-[110%]">Page {`${index + 2}/${waveDatas.length+1}`}</span>
                                    </div> 
                                </div> 
                            </div>
                        </div>
                    )
                })
            }
        </div>
    ); 
}

export default Waveform;
